/**
 * PlantasIotTankCard.jsx  v3
 * Tarjeta de tanque (base plantas_iot) — mismo estilo que TankCard de Alvear
 * Gauge líquido · toneladas disponibles · volumen · última lectura
 * Estado según umbrales configurados en tanques_config
 * v2: toneladas que faltan para el llenado máximo (85%)
 * v3: "tn" al lado del número; faltante más grande y arriba de los porcentajes
 */

import {
    Box, Card, CardContent, Chip, Divider,
    Stack, Typography, alpha, useTheme,
} from '@mui/material';
import {
    WaterDropOutlined as VolumenIcon,
    ScheduleOutlined as RelojIcon,
    WarningAmber as WarningIcon,
} from '@mui/icons-material';
import LiquidGauge from '../alvear/Liquidgauge';

// Llenado máximo permitido de un tanque de GLP (% del volumen)
export const LLENADO_MAX_PCT = 85;

const GRIS = '#9CA3AF';
const VERDE = '#22C55E';
const ROJO = '#EF4444';
const NARANJA = '#F59E0B';

function fmtNum(v, dec = 1) {
    if (v === null || v === undefined || isNaN(v)) return '—';
    return Number(v).toLocaleString('es-AR', { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

// Toneladas que faltan para llegar al llenado máximo, con la densidad actual.
// null si no se puede calcular (sin datos o nivel demasiado bajo para proyectar)
export function faltanteParaMaximo(t) {
    const pct = t.volumen_pct;
    if (pct === null || pct === undefined || t.masa_kg === null || t.masa_kg === undefined) return null;
    if (pct < 1) return null;
    const masaTn = t.masa_kg / 1000;
    return Math.max(0, masaTn * (LLENADO_MAX_PCT - pct) / pct);
}

// Estado de la tarjeta, en orden de prioridad
export function estadoTanque(t, plantaOnline) {
    const pct = t.volumen_pct;
    if (pct === null || pct === undefined) return { clave: 'senal', label: 'SIN SEÑAL', color: GRIS };
    if (!plantaOnline) return { clave: 'stale', label: 'SIN DATOS', color: GRIS };
    if ((t.estado || '').toLowerCase() !== 'ok') return { clave: 'sensor', label: 'FALLA SENSOR', color: NARANJA };
    if (t.alerta_baja_pct !== null && t.alerta_baja_pct !== undefined && pct < t.alerta_baja_pct) {
        return { clave: 'bajo', label: 'NIVEL BAJO', color: ROJO, alerta: true };
    }
    if (t.alerta_alta_pct !== null && t.alerta_alta_pct !== undefined && pct > t.alerta_alta_pct) {
        return { clave: 'alto', label: 'NIVEL ALTO', color: ROJO, alerta: true };
    }
    return { clave: 'ok', label: 'NORMAL', color: VERDE };
}

export default function PlantasIotTankCard({ t, plantaOnline, onClick }) {
    const theme = useTheme();
    const est = estadoTanque(t, plantaOnline);
    const sc = est.color;

    const pct = t.volumen_pct;
    const pctGauge = pct === null || pct === undefined ? 0 : Math.round(Math.min(Math.max(pct, 0), 100));
    const masaTn = t.masa_kg !== null && t.masa_kg !== undefined ? t.masa_kg / 1000 : null;
    const sinValores = est.clave === 'senal';
    const faltaTn = faltanteParaMaximo(t);
    const alMaximo = pct !== null && pct !== undefined && pct >= LLENADO_MAX_PCT;

    const subtitulo = [
        t.producto || null,
        t.capacidad_m3 ? `${fmtNum(t.capacidad_m3, 0)} m³` : null,
    ].filter(Boolean).join(' · ') || '—';

    const umbrales = t.alerta_baja_pct !== null || t.alerta_alta_pct !== null
        ? `alertas ${t.alerta_baja_pct !== null ? fmtNum(t.alerta_baja_pct, 0) + '%' : '—'} / ${t.alerta_alta_pct !== null ? fmtNum(t.alerta_alta_pct, 0) + '%' : '—'}`
        : 'sin umbrales';

    const fecha = t.ts ? new Date(t.ts) : null;

    return (
        <Card
            onClick={onClick}
            elevation={0}
            sx={{
                cursor: 'pointer',
                border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                transition: 'all 0.18s ease',
                position: 'relative',
                overflow: 'hidden',
                bgcolor: 'background.paper',
                height: '100%',
                '&:hover': {
                    borderColor: sc,
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 20px ${alpha(sc, 0.15)}`,
                },
            }}
        >
            {/* Accent top */}
            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, bgcolor: sc, transition: 'background 0.4s ease' }} />

            <CardContent sx={{ p: '14px 16px !important' }}>

                {/* Header: nombre + badge estado */}
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                    <Box>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 12, letterSpacing: 0.5, color: 'text.primary', textTransform: 'uppercase' }}>
                                {t.tanque}
                            </Typography>
                            {est.alerta && (
                                <WarningIcon sx={{
                                    fontSize: 16, color: ROJO,
                                    animation: 'piotBlink 1s infinite',
                                    '@keyframes piotBlink': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0 } },
                                }} />
                            )}
                        </Stack>
                        <Typography variant="caption" display="block" sx={{ color: 'text.disabled', fontSize: 10, lineHeight: 1.3, textTransform: 'capitalize' }}>
                            {subtitulo}
                        </Typography>
                    </Box>
                    <Chip
                        size="small"
                        label={est.label}
                        sx={{
                            height: 22, fontSize: 10, fontWeight: 700,
                            bgcolor: alpha(sc, 0.12),
                            color: est.clave === 'senal' || est.clave === 'stale' ? '#6B7280' : sc,
                            border: `1px solid ${alpha(sc, 0.35)}`,
                            '& .MuiChip-label': { px: 1 },
                        }}
                    />
                </Stack>

                {/* Cuerpo: gauge | masa disponible | divider | volumen + última lectura */}
                <Stack direction="row" alignItems="center" spacing={2}>

                    {/* Gauge líquido animado */}
                    <Box sx={{ flexShrink: 0 }}>
                        <LiquidGauge pct={pctGauge} color={sc} size={110} />
                    </Box>

                    {/* Masa disponible */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 10, display: 'block', mb: 0.3 }}>
                            disponibles
                        </Typography>
                        {sinValores ? (
                            <>
                                <Typography sx={{
                                    fontFamily: "'Orbitron', sans-serif",
                                    fontSize: 22, fontWeight: 700, color: GRIS,
                                    lineHeight: 1.2, letterSpacing: -0.5,
                                }}>
                                    — —
                                </Typography>
                                <Typography variant="caption" display="block" sx={{ color: 'text.disabled', fontSize: 9, mt: 0.4 }}>
                                    último dato no disponible
                                </Typography>
                            </>
                        ) : (
                            <>
                                {/* Toneladas + "tn" en la misma línea */}
                                <Typography sx={{
                                    fontFamily: "'Orbitron', sans-serif",
                                    fontSize: 35, fontWeight: 700,
                                    color: alpha(sc, 0.9),
                                    lineHeight: 1, letterSpacing: -0.5,
                                    whiteSpace: 'nowrap',
                                }}>
                                    {fmtNum(masaTn, 2)}
                                    <Typography component="span" sx={{
                                        fontFamily: 'inherit', fontSize: 14, fontWeight: 600,
                                        color: sc, ml: 0.6, letterSpacing: 0,
                                    }}>
                                        tn
                                    </Typography>
                                </Typography>

                                {/* Faltante para el llenado máximo */}
                                {alMaximo ? (
                                    <Typography sx={{ fontSize: 12, mt: 0.8, fontWeight: 600, color: 'warning.main', lineHeight: 1.3 }}>
                                        al máximo de llenado ({LLENADO_MAX_PCT}%)
                                    </Typography>
                                ) : faltaTn !== null && (
                                    <Typography sx={{ fontSize: 12, mt: 0.8, color: 'text.secondary', lineHeight: 1.3 }}>
                                        faltan{' '}
                                        <Box component="span" sx={{ fontFamily: "'Orbitron', sans-serif", fontSize: 16, fontWeight: 700, color: 'text.primary' }}>
                                            {fmtNum(faltaTn, 2)}
                                        </Box>
                                        {' '}tn para el {LLENADO_MAX_PCT}%
                                    </Typography>
                                )}

                                <Typography variant="caption" display="block" sx={{ color: 'text.disabled', fontSize: 9, mt: 0.4 }}>
                                    {fmtNum(pct, 1)}% · {umbrales}
                                </Typography>
                            </>
                        )}
                    </Box>

                    {/* Divider vertical */}
                    <Divider orientation="vertical" flexItem sx={{ opacity: 0.25 }} />

                    {/* Volumen + última lectura */}
                    <Stack spacing={1.5} sx={{ minWidth: 96, flexShrink: 0 }}>
                        <Box>
                            <Stack direction="row" alignItems="center" spacing={0.4} mb={0.3}>
                                <VolumenIcon sx={{ fontSize: 11, color: 'text.disabled' }} />
                                <Typography variant="caption" sx={{ fontSize: 9, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: 0.6 }}>
                                    Volumen
                                </Typography>
                            </Stack>
                            <Typography sx={{ fontFamily: "'Orbitron', sans-serif", fontSize: 20, fontWeight: 700, color: 'text.primary', lineHeight: 1 }}>
                                {fmtNum(t.volumen_m3, 1)}
                                <Typography component="span" sx={{ fontSize: 12, color: 'text.disabled', fontFamily: 'inherit' }}> m³</Typography>
                            </Typography>
                            {t.capacidad_m3 && (
                                <Typography variant="caption" sx={{ fontSize: 9, color: 'text.disabled' }}>
                                    de {fmtNum(t.capacidad_m3, 0)} m³
                                </Typography>
                            )}
                        </Box>

                        <Divider sx={{ opacity: 0.2 }} />

                        <Box>
                            <Stack direction="row" alignItems="center" spacing={0.4} mb={0.3}>
                                <RelojIcon sx={{ fontSize: 11, color: est.clave === 'stale' ? 'warning.main' : 'text.disabled' }} />
                                <Typography variant="caption" sx={{ fontSize: 9, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: 0.6 }}>
                                    Lectura
                                </Typography>
                            </Stack>
                            <Typography sx={{
                                fontFamily: "'Orbitron', sans-serif", fontSize: 20, fontWeight: 700, lineHeight: 1,
                                color: est.clave === 'stale' ? 'warning.main' : 'text.primary',
                            }}>
                                {fecha ? fecha.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false }) : '—'}
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: 9, color: 'text.disabled' }}>
                                {fecha ? fecha.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' }) : ''}
                            </Typography>
                        </Box>
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    );
}